"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkEntityTypeSelector = void 0;
const react_1 = require("react");
const jsx_runtime_1 = require("react/jsx-runtime");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const design_system_1 = require("@hashintel/design-system");
const material_1 = require("@mui/material");
const react_2 = require("react");
const react_dom_1 = require("react-dom");
const react_hook_form_1 = require("react-hook-form");
const rooks_1 = require("rooks");
const entity_types_options_context_1 = require("../../shared/entity-types-options-context");
const read_only_context_1 = require("../../shared/read-only-context");
const use_filter_type_options_1 = require("../shared/use-filter-type-options");
const TypeChipLabel = ({ children }) => ((0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", spacing: 0.75, fontSize: 14, alignItems: "center", children: [(0, jsx_runtime_1.jsx)(design_system_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faAsterisk, sx: { fontSize: "inherit" } }), (0, jsx_runtime_1.jsx)(material_1.Box, { component: "span", children: children })] }));
const linkEntityTypeSelectorDropdownProps = {
    query: "",
    /**
     * @todo Add a create entity type button
     */
    createButtonProps: null,
    variant: "entityType",
};
const LinkEntityTypeSelector = ({ linkIndex, }) => {
    const { control, setValue } = (0, react_hook_form_1.useFormContext)();
    const isReadonly = (0, read_only_context_1.useIsReadonly)();
    const [entityTypeSelectorPopupOpen, setEntityTypeSelectorPopupOpen] = (0, react_2.useState)(false);
    const entityTypeSelectorRef = (0, react_2.useRef)(null);
    const [tagBorderHeight, setTagBorderHeight] = (0, react_2.useState)(0);
    const [resizeObserverRef] = (0, rooks_1.useResizeObserverRef)(([size]) => {
        if (size) {
            // Using flush sync so that the UI updates immediately, rather than the
            // popup position lagging behind the height of its anchor element
            (0, react_dom_1.flushSync)(() => {
                setTagBorderHeight(size.contentRect.height);
            });
        }
    });
    const { entityTypes } = (0, entity_types_options_context_1.useEntityTypesOptions)();
    const chosenEntityTypeIds = (0, react_hook_form_1.useWatch)({
        control,
        name: `links.${linkIndex}.entityTypes`,
    });
    const entityTypesArray = Object.values(entityTypes);
    const chosenEntityTypes = entityTypesArray.filter((type) => chosenEntityTypeIds.includes(type.$id));
    const entityTypeOptions = (0, use_filter_type_options_1.useFilterTypeOptions)({
        typeOptions: entityTypesArray,
        typesToExclude: chosenEntityTypes,
    });
    /**
     * We change the position of the input which the entity type list popup
     * appears to be attached to based on whether the popup appears above or below
     * the list of tags. However, popper needs to know whether there's space to
     * place the popup above or below, so moving the box the popup is attached to
     * is not an option (it would create an infinite loop). Instead, what we do
     * is (invisibly) make the box the popup is attached to tall enough to contain
     * the input, whether it's positioned above or below the tags, and reposition
     * the input within that box based on where the popup appears. This value
     * represents the extra space needed either above or below the tags.
     */
    const selectorOffset = design_system_1.TYPE_SELECTOR_HEIGHT + tagBorderHeight;
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: (theme) => ({
            position: "relative",
            ...(entityTypeSelectorPopupOpen
                ? { zIndex: theme.zIndex.modal + 1 }
                : { "&, *": { cursor: isReadonly ? "default" : "pointer" } }),
            [entityTypeSelectorPopupOpen ? "& > *" : "&:hover > *"]: isReadonly
                ? {}
                : {
                    boxShadow: theme.boxShadows.xs,
                    borderColor: `${theme.palette.gray[30]} !important`,
                    backgroundColor: "white",
                },
        }), ref: entityTypeSelectorRef, children: [(0, jsx_runtime_1.jsx)(material_1.Stack, { direction: "row", flexWrap: "wrap", sx: [
                    (theme) => ({
                        border: 1,
                        borderColor: "transparent",
                        borderRadius: 1.5,
                        p: 0.5,
                        userSelect: "none",
                        minWidth: 200,
                        minHeight: 42,
                        left: -7,
                        width: "calc(100% + 14px)",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: theme.zIndex.drawer,
                    }),
                ], onClick: (evt) => {
                    evt.preventDefault();
                    if (isReadonly) {
                        return;
                    }
                    setEntityTypeSelectorPopupOpen(true);
                }, ...(entityTypeSelectorPopupOpen
                    ? {
                        onMouseDown: (evt) => {
                            // This prevents the input field blurring when you click on
                            // tags. Because the field blurring hides the popup, and
                            // therefore the delete icons, without this line you wouldn't
                            // be able to hit the delete icons on tags
                            evt.preventDefault();
                        },
                    }
                    : {}), children: chosenEntityTypeIds.length ? (chosenEntityTypeIds.map((entityTypeId) => {
                    const type = entityTypes[entityTypeId];
                    if (!type) {
                        throw new Error("Entity type missing in links table");
                    }
                    return ((0, react_1.createElement)(design_system_1.Chip, { sx: { m: 0.25 }, tabIndex: -1, ...(entityTypeSelectorPopupOpen
                            ? {
                                onDelete: () => {
                                    setValue(`links.${linkIndex}.entityTypes`, chosenEntityTypeIds.filter((id) => id !== entityTypeId), { shouldDirty: true });
                                },
                            }
                            : {}), color: "blue", label: (0, jsx_runtime_1.jsx)(TypeChipLabel, { children: type.title }), key: type.$id }));
                })) : ((0, jsx_runtime_1.jsx)(design_system_1.Chip, { color: "blue", variant: "outlined", label: (0, jsx_runtime_1.jsx)(TypeChipLabel, { children: "Anything" }) })) }), entityTypeSelectorPopupOpen ? ((0, jsx_runtime_1.jsx)(material_1.Box, { ref: resizeObserverRef, onClick: (evt) => {
                    evt.stopPropagation();
                    evt.preventDefault();
                }, sx: [
                    (theme) => ({
                        position: "absolute",
                        left: -19,
                        right: -19,
                        top: -12,
                        bottom: -12,
                        background: "white",
                        borderRadius: 1.5,
                        border: 1,
                        borderColor: theme.palette.gray[30],
                        boxShadow: theme.boxShadows.md,
                    }),
                    design_system_1.popperPlacementInputNoRadius,
                ], children: (0, jsx_runtime_1.jsx)(design_system_1.SelectorAutocomplete, { disableCloseOnSelect: true, multiple: true, sx: [
                        design_system_1.popperPlacementPopperNoRadius,
                        {
                            minWidth: 440,
                            position: "absolute",
                            left: -1,
                            width: "calc(100% + 2px)",
                            // This ensures there is enough space for the input, above or
                            // below
                            height: design_system_1.TYPE_SELECTOR_HEIGHT + selectorOffset,
                            top: `calc(100% - ${selectorOffset}px)`,
                            paddingTop: `${selectorOffset}px`,
                            [`${design_system_1.popperPlacementSelectors.top} &`]: {
                                paddingTop: 0,
                            },
                            // We need this box not to mask clicks
                            pointerEvents: "none",
                            "> *": {
                                pointerEvents: "all",
                            },
                        },
                    ], 
                    // Whenever the input is rendered, the popup is also rendered
                    open: true, onChange: (_, __, reason, details) => {
                        /**
                         * MUI has weird behaviour with sorting its value internally, which
                         * if we used that to set our value the tags would jump around.
                         * To avoid that problem, we handle adding/removing values from
                         * our own value manually
                         */
                        if (details) {
                            switch (reason) {
                                case "selectOption":
                                    setValue(`links.${linkIndex}.entityTypes`, [...chosenEntityTypeIds, details.option.$id], { shouldDirty: true });
                                    break;
                                case "removeOption":
                                    setValue(`links.${linkIndex}.entityTypes`, chosenEntityTypeIds.filter((id) => id !== details.option.$id), { shouldDirty: true });
                                    break;
                            }
                        }
                        return false;
                    }, options: entityTypeOptions, optionToRenderData: ({ $id, title, description }) => ({
                        $id,
                        title,
                        description,
                    }), dropdownProps: linkEntityTypeSelectorDropdownProps, joined: true, 
                    // We render our tags manually, so we don't want MUI doing it for us
                    renderTags: () => (0, jsx_runtime_1.jsx)(material_1.Box, {}), value: chosenEntityTypes, modifiers: [
                        /**
                         * We need to respond to whether the popup is above or below using
                         * CSS at a higher level than our addPositionSelector modifier sets
                         * the data attribute usually. But the modifier will notify us, so
                         * we can set it on a node we can choose.
                         */
                        {
                            name: "addPositionSelector",
                            phase: "write",
                            options: {
                                update(placement) {
                                    const node = entityTypeSelectorRef.current;
                                    if (node) {
                                        (0, design_system_1.setPopperPlacementAttribute)(node, placement);
                                    }
                                },
                            },
                        },
                    ], onKeyDown: (evt) => {
                        if (evt.key === "Escape") {
                            setEntityTypeSelectorPopupOpen(false);
                        }
                    }, onBlur: () => {
                        setEntityTypeSelectorPopupOpen(false);
                    } }) })) : null] }));
};
exports.LinkEntityTypeSelector = LinkEntityTypeSelector;
